// @ts-nocheck
import { useState, useEffect } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  SimpleGrid,
  Badge,
  ActionIcon,
  ThemeIcon,
  Loader,
  Center,
} from '@mantine/core';
import { IconPlus, IconTemplate, IconCopy, IconTrash, IconClock } from '@tabler/icons-react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { useDisclosure } from '@mantine/hooks';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useScheduleTemplates } from '../../hooks/useScheduleTemplates';

interface TemplateShift {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

interface ScheduleTemplate {
  id: string;
  name: string;
  description: string;
  shifts: TemplateShift[];
  isDefault: boolean;
  createdAt: string;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ShiftSummary({ shifts }: { shifts: TemplateShift[] }) {
  const byDay = new Map<number, TemplateShift[]>();
  for (const shift of shifts) {
    if (!byDay.has(shift.dayOfWeek)) byDay.set(shift.dayOfWeek, []);
    byDay.get(shift.dayOfWeek)!.push(shift);
  }

  const activeDays = Array.from(byDay.keys()).sort();

  if (activeDays.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        No shifts defined
      </Text>
    );
  }

  return (
    <Group gap={4} wrap="wrap">
      {DAY_LABELS.map((label, index) => {
        const dayShifts = byDay.get(index);
        return (
          <Badge
            key={index}
            size="xs"
            variant={dayShifts ? 'filled' : 'light'}
            color={dayShifts ? 'blue' : 'gray'}
          >
            {label}
          </Badge>
        );
      })}
    </Group>
  );
}

function totalWeeklyHours(shifts: TemplateShift[]): number {
  let total = 0;
  for (const shift of shifts) {
    const [startH = 0, startM = 0] = shift.startTime.split(':').map(Number);
    const [endH = 0, endM = 0] = shift.endTime.split(':').map(Number);
    const hours = endH - startH + (endM - startM) / 60;
    if (hours > 0) total += hours;
  }
  return total;
}

export default function ScheduleTemplatePage() {
  const { data: rawQueryData, loading, error } = useScheduleTemplates();
  const queryData = rawQueryData as any;
  const templates = (queryData?.scheduleTemplates ?? []) as ScheduleTemplate[];
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', description: '' });

  useEffect(() => {
    if (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load schedule templates',
        color: 'red',
      });
    }
  }, [error]);

  const handleCreate = () => {
    // Template creation would trigger mutation
    closeAdd();
    setNewTemplate({ name: '', description: '' });
    notifications.show({
      title: 'Template Created',
      message: `"${newTemplate.name}" has been created.`,
      color: 'green',
    });
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Schedule Templates"
        subtitle="Create and manage reusable schedule templates"
        breadcrumbs={[
          { label: 'Schedule', to: '/admin/workforce/schedules' },
          { label: 'Templates' },
        ]}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={openAdd}>
            New Template
          </Button>
        }
      />

      {loading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : templates.length === 0 ? (
        <EmptyState
          icon={<IconTemplate size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="No Templates"
          message="Create a schedule template to quickly assign recurring shift patterns."
          action={{ label: 'Create Template', onClick: openAdd }}
        />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }}>
          {templates.map((template) => (
            <Paper key={template.id} withBorder p="lg" radius="md">
              <Group justify="space-between" mb="sm">
                <Group gap="sm">
                  <ThemeIcon size="md" variant="light" color="blue">
                    <IconTemplate size={16} />
                  </ThemeIcon>
                  <div>
                    <Text size="sm" fw={600}>
                      {template.name}
                    </Text>
                    {template.isDefault && (
                      <Badge size="xs" variant="light" color="green">
                        Default
                      </Badge>
                    )}
                  </div>
                </Group>
                <Group gap={4}>
                  <ActionIcon variant="subtle" size="sm" color="gray">
                    <IconCopy size={14} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" size="sm" color="red">
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>
              </Group>

              {template.description && (
                <Text size="xs" c="dimmed" mb="sm" lineClamp={2}>
                  {template.description}
                </Text>
              )}

              <ShiftSummary shifts={template.shifts} />

              <Group gap="xs" mt="sm">
                <IconClock size={14} color="var(--mantine-color-dimmed)" />
                <Text size="xs" c="dimmed">
                  {totalWeeklyHours(template.shifts).toFixed(1)}h / week
                </Text>
                <Text size="xs" c="dimmed">
                  {template.shifts.length} shifts
                </Text>
              </Group>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      <Modal opened={addOpened} onClose={closeAdd} title="New Schedule Template" size="md">
        <Stack gap="sm">
          <TextInput
            label="Template Name"
            placeholder="e.g., Standard 9-5"
            required
            value={newTemplate.name}
            onChange={(e) =>
              setNewTemplate((f) => ({ ...f, name: e.currentTarget.value }))
            }
          />
          <Textarea
            label="Description"
            placeholder="Describe this schedule template..."
            value={newTemplate.description}
            onChange={(e) =>
              setNewTemplate((f) => ({ ...f, description: e.currentTarget.value }))
            }
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeAdd}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!newTemplate.name}>
              Create Template
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
